import { Vector2 } from '../../../engine/ecs/core/Vector2';

/**
 * Maze cell types
 */
export enum CellType {
  Empty = 0, // Empty space
  Wall = 1, // Wall
  Dot = 2, // Small dot
  PowerPellet = 3, // Power pellet
  Ghost = 4, // Ghost spawn point
  Pacman = 5, // Pacman spawn point
  Tunnel = 6, // Tunnel entrance/exit
}

export const MUDDY_MAZE_GAME_BACKGROUND_IMAGE = '/GameAssets/images/mud2.avif';
export const MUDDY_MAZE_WRAPPER_BACKGROUND_IMAGE =
  '/GameAssets/images/mud2.avif';
/**
 * Maze level data structure
 */
export interface MazeLevel {
  width: number;
  height: number;
  cellSize: number;
  maze: number[][];
  pacmanStartPosition: Vector2;
  ghostStartPositions: Vector2[];
  totalDots: number;
  powerPelletPositions: Vector2[];
  tunnelPositions: Vector2[];
}

/**
 * MazeData - Contains all maze layouts and level data
 */
export class MazeData {
  /**
   * Classic Pacman maze layout
   */
  private static readonly CLASSIC_MAZE: number[][] = [
    [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 3, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 2, 1, 1, 1,
      1, 3, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 1, 1, 0, 0, 0, 0, 1, 1, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      6, 0, 0, 0, 0, 0, 2, 0, 0, 0, 1, 4, 4, 4, 4, 4, 4, 1, 0, 0, 0, 2, 0, 0, 0,
      0, 0, 6,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 2, 1, 1, 1,
      1, 2, 1,
    ],
    [
      1, 3, 2, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2,
      2, 3, 1,
    ],
    [
      1, 1, 1, 2, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 2,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 1,
    ],
  ];

  /**
   * Simple seeded random number generator
   */
  private static seededRandom(seed: string): () => number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    let state = Math.abs(hash);
    return () => {
      state = (state * 9301 + 49297) % 233280;
      return state / 233280;
    };
  }

  /**
   * Alternative maze variant 1 - More open layout
   */
  private static readonly VARIANT_MAZE_1: number[][] = [
    [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 3, 1, 1, 2, 2, 2, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 2, 2, 2, 1,
      1, 3, 1,
    ],
    [
      1, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 2, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 2, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 1, 2, 2, 2, 2, 1, 1, 2, 1, 1, 2, 1, 1, 2, 2, 2, 2, 1, 2, 2,
      2, 2, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      6, 0, 0, 0, 0, 0, 2, 0, 0, 0, 1, 4, 4, 4, 4, 4, 4, 1, 0, 0, 0, 2, 0, 0, 0,
      0, 0, 6,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 1, 1, 2, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2, 1, 1, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 2, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 2, 1,
      1, 2, 1,
    ],
    [
      1, 3, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 3, 1,
    ],
    [
      1, 2, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 1,
    ],
  ];

  /**
   * Alternative maze variant 2 - More corridors
   */
  private static readonly VARIANT_MAZE_2: number[][] = [
    [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 3, 1, 1, 1, 2, 1, 1, 1, 1, 1, 2, 2, 1, 1, 2, 2, 1, 1, 1, 1, 1, 2, 1, 1,
      1, 3, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 1, 2, 2, 2, 2, 1, 1, 2, 2, 1, 1, 2, 2, 1, 1, 2, 2, 2, 2, 1, 2,
      2, 2, 1,
    ],
    [
      1, 1, 1, 2, 1, 2, 1, 1, 1, 1, 1, 2, 0, 1, 1, 0, 2, 1, 1, 1, 1, 1, 2, 1, 2,
      1, 1, 1,
    ],
    [
      1, 1, 1, 2, 1, 2, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 2, 1, 2,
      1, 1, 1,
    ],
    [
      1, 1, 1, 2, 1, 2, 1, 1, 0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 0, 1, 1, 2, 1, 2,
      1, 1, 1,
    ],
    [
      6, 0, 0, 2, 0, 2, 0, 0, 0, 1, 4, 4, 4, 4, 4, 4, 4, 4, 1, 0, 0, 0, 2, 0, 2,
      0, 0, 6,
    ],
    [
      1, 1, 1, 2, 1, 2, 1, 1, 0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 0, 1, 1, 2, 1, 2,
      1, 1, 1,
    ],
    [
      1, 1, 1, 2, 1, 2, 1, 1, 0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 1, 1, 2, 1, 2,
      1, 1, 1,
    ],
    [
      1, 1, 1, 2, 1, 2, 1, 1, 1, 1, 1, 2, 0, 1, 1, 0, 2, 1, 1, 1, 1, 1, 2, 1, 2,
      1, 1, 1,
    ],
    [
      1, 2, 2, 2, 1, 2, 2, 2, 2, 1, 1, 2, 2, 1, 1, 2, 2, 1, 1, 2, 2, 2, 2, 1, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 1, 1,
      1, 2, 1,
    ],
    [
      1, 3, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 3, 1,
    ],
    [
      1, 2, 1, 1, 2, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 2, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2, 1, 1, 2, 2, 2, 2, 1, 1, 2, 2,
      2, 2, 1,
    ],
    [
      1, 2, 1, 1, 1, 1, 1, 1, 1, 1, 2, 1, 1, 2, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 1,
      1, 2, 1,
    ],
    [
      1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
      2, 2, 1,
    ],
    [
      1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
      1, 1, 1,
    ],
  ];

  /**
   * Select a maze variant based on seed
   *
   * This is STEP 1 of the Mystic World generation:
   * Randomly selects one of 3 pre-designed mazes, each with DIFFERENT wall structures.
   * This is what makes Mystic World look different - different walls, not just shuffled dots!
   */
  private static selectMazeVariant(seed: string): number[][] {
    const random = this.seededRandom(seed);
    const variant = Math.floor(random() * 3); // 0, 1, or 2

    let selected: number[][];
    switch (variant) {
      case 0:
        selected = this.CLASSIC_MAZE.map((row) => [...row]);
        console.log(
          `[MazeData] Selected CLASSIC variant (0) for seed: ${seed}`
        );
        break;
      case 1:
        selected = this.VARIANT_MAZE_1.map((row) => [...row]);
        console.log(
          `[MazeData] Selected VARIANT_1 (more open) for seed: ${seed}`
        );
        break;
      case 2:
        selected = this.VARIANT_MAZE_2.map((row) => [...row]);
        console.log(
          `[MazeData] Selected VARIANT_2 (more corridors) for seed: ${seed}`
        );
        break;
      default:
        selected = this.CLASSIC_MAZE.map((row) => [...row]);
        console.log(`[MazeData] Selected CLASSIC (default) for seed: ${seed}`);
    }

    // Debug: Show wall pattern signature (first few cells)
    const signature = selected
      .slice(0, 3)
      .map((row) => row.slice(0, 5).join(','))
      .join('|');
    console.log(
      `[MazeData] Variant signature (first 3 rows, first 5 cols): ${signature}`
    );

    return selected;
  }

  /**
   * Generate a random maze variation based on seed while preserving game invariants
   *
   * This is STEP 2 of the Mystic World generation:
   * Takes the selected variant (from selectMazeVariant) and randomizes ONLY dots and power pellets.
   * The wall structure from the selected variant is preserved exactly to ensure collision detection works.
   */
  private static generateRandomMaze(
    baseMaze: number[][],
    seed: string
  ): number[][] {
    const maze = baseMaze.map((row) => [...row]);
    const random = this.seededRandom(seed);
    const width = maze[0].length;
    const height = maze.length;

    // The baseMaze parameter already has the selected variant's wall structure.
    // We preserve those walls exactly and only randomize dots/pellets for variety.

    // Collect all path cells (dots, empty, power pellets) that can be randomized
    const pathCells: Array<[number, number, number]> = [];
    const preservedCells = new Map<string, number>();

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const val = maze[y][x];
        // Preserve walls, tunnels, pacman, and ghost spawns exactly
        if (
          val === CellType.Wall ||
          val === CellType.Tunnel ||
          val === CellType.Pacman ||
          val === CellType.Ghost
        ) {
          preservedCells.set(`${x},${y}`, val);
        } else {
          // Collect all path cells (dots, empty, power pellets) for randomization
          pathCells.push([x, y, val]);
        }
      }
    }

    // Separate power pellets to preserve their count
    const powerPelletCells: Array<[number, number]> = [];
    const dotAndEmptyCells: Array<[number, number]> = [];

    for (const [x, y, val] of pathCells) {
      if (val === CellType.PowerPellet) {
        powerPelletCells.push([x, y]);
      } else {
        dotAndEmptyCells.push([x, y]);
      }
    }

    // Shuffle power pellet positions based on seed
    for (let i = powerPelletCells.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [powerPelletCells[i], powerPelletCells[j]] = [
        powerPelletCells[j],
        powerPelletCells[i],
      ];
    }

    // Shuffle dot/empty positions
    for (let i = dotAndEmptyCells.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [dotAndEmptyCells[i], dotAndEmptyCells[j]] = [
        dotAndEmptyCells[j],
        dotAndEmptyCells[i],
      ];
    }

    // Clear all path cells first (but keep walls!)
    for (const [x, y] of dotAndEmptyCells) {
      maze[y][x] = CellType.Empty;
    }
    for (const [x, y] of powerPelletCells) {
      maze[y][x] = CellType.Empty;
    }

    // Place power pellets randomly in path cells (ensure we have at least 4)
    const numPowerPellets = Math.max(4, Math.min(powerPelletCells.length, 6));
    const allPathCells = [...dotAndEmptyCells, ...powerPelletCells];

    // Shuffle all path cells together for final placement
    for (let i = allPathCells.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [allPathCells[i], allPathCells[j]] = [allPathCells[j], allPathCells[i]];
    }

    // Place power pellets first
    for (let i = 0; i < numPowerPellets && i < allPathCells.length; i++) {
      const [x, y] = allPathCells[i];
      maze[y][x] = CellType.PowerPellet;
    }

    // Place dots in remaining cells (80% dots, 20% empty for variety)
    for (let i = numPowerPellets; i < allPathCells.length; i++) {
      const [x, y] = allPathCells[i];
      if (random() < 0.8) {
        maze[y][x] = CellType.Dot;
      } else {
        maze[y][x] = CellType.Empty;
      }
    }

    // Restore all preserved cells (walls, tunnels, spawns)
    for (const [key, val] of preservedCells.entries()) {
      const [x, y] = key.split(',').map(Number);
      maze[y][x] = val;
    }

    return maze;
  }

  /**
   * Get maze data for a specific level
   */
  public static getLevel(
    levelNumber: number,
    options?: { mazeMode?: 'fixed' | 'random'; mazeSeed?: string }
  ): MazeLevel {
    let baseMaze: number[][];

    // STEP 1: Select base maze variant for random mode (this changes wall structure!)
    if (options?.mazeMode === 'random' && options?.mazeSeed) {
      // Combine seed with level number for variety across levels
      const combinedSeed = `${options.mazeSeed}-${levelNumber}`;
      baseMaze = this.selectMazeVariant(combinedSeed); // Selects one of 3 mazes with different walls
    } else {
      baseMaze = this.CLASSIC_MAZE.map((row) => [...row]);
    }

    // STEP 2: Randomize dots/pellets on the selected variant (preserves walls)
    let maze = baseMaze;
    if (options?.mazeMode === 'random' && options?.mazeSeed) {
      const combinedSeed = `${options.mazeSeed}-${levelNumber}`;
      maze = this.generateRandomMaze(baseMaze, combinedSeed); // Only randomizes dots/pellets
    }

    return {
      width: maze[0].length,
      height: maze.length,
      cellSize: 24,
      maze: maze,
      pacmanStartPosition: this.findPacmanStart(maze),
      ghostStartPositions: this.findGhostStarts(maze),
      totalDots: this.countDots(maze),
      powerPelletPositions: this.findPowerPellets(maze),
      tunnelPositions: this.findTunnels(maze),
    };
  }

  /**
   * Find Pacman's starting position in the maze
   */
  private static findPacmanStart(maze: number[][]): Vector2 {
    for (let y = 0; y < maze.length; y++) {
      for (let x = 0; x < maze[y].length; x++) {
        if (maze[y][x] === CellType.Pacman) {
          return new Vector2(x * 24 + 12, y * 24 + 12); // Center of cell
        }
      }
    }
    return new Vector2(336, 276); // Default fallback position
  }

  /**
   * Find ghost starting positions in the maze
   */
  private static findGhostStarts(maze: number[][]): Vector2[] {
    const positions: Vector2[] = [];

    // Place ghosts outside the base area so they can move freely
    // These positions are in the main maze corridors near the base

    // Blinky (Red) - Above the base, ready to chase
    positions.push(new Vector2(13 * 24 + 12, 7 * 24 + 12));

    // Pinky (Pink) - Left side of maze
    positions.push(new Vector2(2 * 24 + 12, 9 * 24 + 12));

    // Inky (Cyan) - Right side of maze
    positions.push(new Vector2(25 * 24 + 12, 9 * 24 + 12));

    // Clyde (Orange) - Bottom area
    positions.push(new Vector2(13 * 24 + 12, 15 * 24 + 12));

    return positions;
  }

  /**
   * Count the total number of dots in the maze
   */
  private static countDots(maze: number[][]): number {
    let count = 0;
    for (let y = 0; y < maze.length; y++) {
      for (let x = 0; x < maze[y].length; x++) {
        if (maze[y][x] === CellType.Dot) {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Find all power pellet positions
   */
  private static findPowerPellets(maze: number[][]): Vector2[] {
    const positions: Vector2[] = [];
    for (let y = 0; y < maze.length; y++) {
      for (let x = 0; x < maze[y].length; x++) {
        if (maze[y][x] === CellType.PowerPellet) {
          positions.push(new Vector2(x * 24 + 12, y * 24 + 12));
        }
      }
    }
    return positions;
  }

  /**
   * Find tunnel entrance/exit positions
   */
  private static findTunnels(maze: number[][]): Vector2[] {
    const positions: Vector2[] = [];
    for (let y = 0; y < maze.length; y++) {
      for (let x = 0; x < maze[y].length; x++) {
        if (maze[y][x] === CellType.Tunnel) {
          positions.push(new Vector2(x * 24 + 12, y * 24 + 12));
        }
      }
    }
    return positions;
  }

  /**
   * Check if a position is a wall
   */
  public static isWall(maze: number[][], x: number, y: number): boolean {
    if (y < 0 || y >= maze.length || x < 0 || x >= maze[0].length) {
      return true; // Out of bounds is considered a wall
    }
    return maze[y][x] === CellType.Wall;
  }

  /**
   * Check if a position is valid for movement
   */
  public static isValidPosition(
    maze: number[][],
    x: number,
    y: number
  ): boolean {
    return !this.isWall(maze, x, y);
  }

  /**
   * Convert pixel coordinates to grid coordinates
   */
  public static pixelToGrid(
    pixelX: number,
    pixelY: number,
    cellSize: number = 24
  ): Vector2 {
    return new Vector2(
      Math.floor(pixelX / cellSize),
      Math.floor(pixelY / cellSize)
    );
  }

  /**
   * Convert grid coordinates to pixel coordinates (center of cell)
   */
  public static gridToPixel(
    gridX: number,
    gridY: number,
    cellSize: number = 24
  ): Vector2 {
    return new Vector2(
      gridX * cellSize + cellSize / 2,
      gridY * cellSize + cellSize / 2
    );
  }
}
